import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const URL_PARAM_KEY = 'breakerId';

const isValidBreakerId = (id: number) => {
  if (Number.isNaN(id)) {
    return false;
  }

  return 0 <= id && id < 10000;
};

const useBreakerModal = () => {
  const [selectedBreakerId, setSelectedBreakerId] = useState<number | null>(null);

  const { pathname, search } = useLocation();
  const navigate = useNavigate();

  const setId = useCallback(
    (id: number, replace = false) => {
      const searchParams = new URLSearchParams(search);
      searchParams.set(URL_PARAM_KEY, `${id}`);

      navigate(
        {
          pathname,
          search: searchParams.toString(),
        },
        {
          replace,
        },
      );
    },
    [navigate, pathname, search],
  );

  const clearId = useCallback(
    (replace = false) => {
      const searchParams = new URLSearchParams(search);

      searchParams.delete(URL_PARAM_KEY);

      navigate(
        {
          pathname,
          search: searchParams.toString(),
        },
        {
          replace,
        },
      );
    },
    [navigate, pathname, search],
  );

  useEffect(() => {
    const searchParams = new URLSearchParams(search);

    if (searchParams.has(URL_PARAM_KEY)) {
      const maybeBreakerId = searchParams.get(URL_PARAM_KEY);

      if (maybeBreakerId) {
        const breakerId = parseInt(maybeBreakerId, 10);

        if (isValidBreakerId(breakerId)) {
          setSelectedBreakerId(breakerId);
          setId(breakerId, true);
          return;
        }
      }

      clearId(true);
    } else {
      if (selectedBreakerId) {
        setSelectedBreakerId(null);
      }
    }
  }, [search, selectedBreakerId, clearId, setId]);

  return {
    setId,
    clearId,
    selectedBreakerId,
  };
};

export default useBreakerModal;
